import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { AlertTriangle, CalendarClock, ShieldCheck } from "lucide-react";

const ORIZZONTI = [30, 60, 90, 180];

/**
 * Il semaforo delle persone.
 *
 * La parte che conta e' la prima: chi e' gia' segnato in pianificazione per un
 * giorno in cui avra' un documento obbligatorio scaduto. E' l'unica vista che
 * guarda avanti invece di raccontare cosa e' successo, e serve a spostare una
 * persona prima che si presenti in cantiere scoperta.
 */
export default function SemaforoPersone() {
  const [giorni, setGiorni] = useState(90);
  const [dati, setDati] = useState(null);
  const [caricando, setCaricando] = useState(true);

  const carica = useCallback(async () => {
    setCaricando(true);
    try {
      const { data } = await api.get(`/reports/semaforo-persone?giorni=${giorni}`);
      setDati(data);
    } catch { toast.error("Errore caricamento semaforo persone"); }
    finally { setCaricando(false); }
  }, [giorni]);

  useEffect(() => { carica(); }, [carica]);

  const tono = (s) => {
    if (s.gia_scaduto) return "text-red-700 bg-red-50 border-red-200";
    if (s.giorni <= 30) return "text-amber-800 bg-amber-50 border-amber-200";
    return "text-slate-600 bg-slate-50 border-slate-200";
  };

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-display text-lg font-bold">Semaforo delle persone</h3>
          <p className="text-xs text-slate-500 mt-1">
            Solo i documenti obbligatori per la mansione. Chi non opera in cantiere non compare.
          </p>
        </div>
        <div className="flex gap-1">
          {ORIZZONTI.map(g => (
            <button key={g} type="button" onClick={() => setGiorni(g)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                      giorni === g ? "bg-orange-500 text-white border-orange-500"
                                   : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>
              {g} giorni
            </button>
          ))}
        </div>
      </div>

      {caricando && <p className="text-sm text-slate-500 py-6 text-center">Caricamento...</p>}

      {!caricando && dati && (<>
        {/* --- il pezzo che guarda avanti ---------------------------------- */}
        {dati.conflitti.length > 0 ? (
          <Card className="border-red-200 bg-red-50/40">
            <CardContent className="p-5 space-y-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-display font-bold text-red-800">
                    {dati.persone_in_conflitto} person{dati.persone_in_conflitto === 1 ? "a" : "e"} in
                    cantiere con un documento scaduto
                  </div>
                  <p className="text-xs text-red-700 mt-0.5">
                    Sono già in pianificazione per giorni in cui il documento non sarà più valido.
                  </p>
                </div>
              </div>

              <div className="border border-red-200 rounded bg-white overflow-x-auto">
                <table className="w-full text-sm min-w-[640px]">
                  <thead className="bg-red-50 text-[10px] uppercase text-red-800">
                    <tr>
                      <th className="text-left p-2">Giorno</th>
                      <th className="text-left p-2">Persona</th>
                      <th className="text-left p-2">Documento</th>
                      <th className="text-left p-2">Scaduto il</th>
                      <th className="text-left p-2">Dove</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dati.conflitti.map((c, i) => (
                      <tr key={i} className="border-t border-red-100">
                        <td className="p-2 font-semibold whitespace-nowrap">{c.data}</td>
                        <td className="p-2">
                          <span className="font-medium">{c.dipendente}</span>
                          <div className="text-[10px] text-slate-500">{c.mansione}</div>
                        </td>
                        <td className="p-2">{c.documento}</td>
                        <td className="p-2 text-red-700 whitespace-nowrap">{c.scade_il}</td>
                        <td className="p-2 text-slate-600">
                          {c.cantiere}
                          <div className="text-[10px] text-slate-400">{c.squadra}</div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-emerald-200 bg-emerald-50/40">
            <CardContent className="p-4 flex items-center gap-2 text-sm text-emerald-800">
              <ShieldCheck className="w-5 h-5 flex-shrink-0" />
              Nessuno è pianificato per un giorno in cui avrà un documento scaduto.
            </CardContent>
          </Card>
        )}

        {/* --- cosa scade e quando ------------------------------------------ */}
        <Card className="border-slate-200">
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center gap-2">
              <CalendarClock className="w-4 h-4 text-slate-400" />
              <h4 className="font-display font-bold text-sm">Scadenze nei prossimi {giorni} giorni</h4>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {[
                { l: "Già scaduti", v: dati.gia_scaduti, c: "border-red-200 bg-red-50 text-red-700" },
                { l: "Entro 30 giorni", v: dati.entro_30, c: "border-amber-200 bg-amber-50 text-amber-700" },
                { l: "Entro 60 giorni", v: dati.entro_60, c: "border-slate-200 bg-slate-50 text-slate-700" },
                { l: "Oltre 60 giorni", v: dati.oltre_60, c: "border-slate-200 bg-slate-50 text-slate-700" },
              ].map(x => (
                <div key={x.l} className={`border rounded p-2 text-center ${x.c}`}>
                  <div className="text-lg font-bold leading-none">{x.v}</div>
                  <div className="text-[10px] uppercase tracking-wide mt-1">{x.l}</div>
                </div>
              ))}
            </div>

            {dati.scadenze.length === 0 ? (
              <p className="text-sm text-slate-500 py-4 text-center">
                Nessuna scadenza nei prossimi {giorni} giorni.
              </p>
            ) : (
              <div className="divide-y divide-slate-100 max-h-96 overflow-y-auto">
                {dati.scadenze.map((s, i) => (
                  <div key={i} className="py-2 flex items-center justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="font-medium text-sm">
                        {s.dipendente}
                        {s.badge && <span className="text-slate-400 font-normal"> · tessera {s.badge}</span>}
                      </div>
                      <div className="text-xs text-slate-500 truncate">
                        {s.documento}{s.titolo ? ` · ${s.titolo}` : ""}
                      </div>
                    </div>
                    <Badge className={`border text-[11px] ${tono(s)}`}>
                      {s.gia_scaduto
                        ? `scaduto il ${s.scade_il}`
                        : `fra ${s.giorni} giorn${s.giorni === 1 ? "o" : "i"} · ${s.scade_il}`}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </>)}
    </div>
  );
}
